import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { IFacilityRoles, useHfBranch, useModal } from "hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { createClient, deleteClient, getAllClients } from "service";
import { IAddUserForm } from "types";
import { getTokenFromLocalStore } from "utils";
import debounce from "lodash.debounce"
import { toast } from "sonner";

export interface IAllClientsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: IClientUser[];
}

export interface IClient {
  pk: number;
  user: IClientUser;
  health_facility: number;
  branch: number;
  pregnancy_status: string;
  is_dependent: boolean;
}

export interface IClientUser {
  pk: number;
  health_facility: number;
  branch: number;
  pregnancy_status: string;
  address: string;
  history_of_allergies: string;
  id_card: string | null;
  vaccination_count: number;
  shot_count: number;
  health_facility_name: string;
  ino_id: string;
  email: string;
  notification_email: any | null;
  phone: string;
  whatsapp_number: string | null;
  surname: string;
  firstname: string;
  other_names: string;
  gender: string;
  is_active: boolean;
  fullname: string;
  is_technical_officer: boolean;
  is_family_account: boolean;
  occupation: string | null;
  height: string | null;
  weight: string | null;
  dob: string | null;
  age: string | null;
}

export function useClients() {
  const queryClient = useQueryClient();
  const { branch, allBranchesOptions } = useHfBranch();
  const [pagination, setPagination] = useState<{ current: number; pageSize: number }>({
    current: 1,
    pageSize: 10,
  });
  const [selectedUser, setSelectedUser] = useState<IClientUser | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState<IAddUserForm>({
    user_id: "",
    branch: 0,
    pregnancy_status: false,
    address: "",
    history_of_allergies: "",
    id_card: "",
  });



  const {
    isOpen: isAddClientModal,
    openModal: openAddClientModal,
    closeModal: closeAddClientModal,
  } = useModal();

  const {
    isOpen: isViewClientModal,
    openModal: openViewClientModal,
    closeModal: closeViewClientModal,
  } = useModal();

  const {
    data: allClients,
    isPending: allClientsLoading,
    error: allClientsError,
    refetch
  } = useQuery<IAllClientsResponse>({
    queryKey: ["list-clients", branch?.hfId, page, searchTerm],
    queryFn: async () => {
      if (!branch) return;
      const response = await getAllClients({
        hfId: branch.hfId,
        page: pagination.current,
        size: pagination.pageSize,
        search: searchTerm,
      });
      return response;
    },
    enabled: !!branch,
  });

  const handlePageChange = (page: number, pageSize?: number) => {
    const newPage = Math.max(1, page);
    setPagination((prev) => ({
      current: newPage,
      pageSize: pageSize ?? prev.pageSize,
    }));
    refetch();
  };

  const { mutate: addClientMutation, isPending: addClientIsPending } =
    useMutation<IClient>({
      mutationFn: async () => {
        if (!branch) return;
        const form = new FormData();

        form.append("user_id", formData.user_id);
        form.append("branch", formData.branch.toString());
        form.append("pregnancy_status", formData.pregnancy_status.toString());
        form.append("address", formData.address);
        form.append("history_of_allergies", formData.history_of_allergies);

        const response = await createClient(branch.hfId, form);
        return response;
      },
      onSuccess: async (data) => {
        await queryClient.invalidateQueries({
          queryKey: ["list-clients", branch?.hfId, page],
        });
        closeAddClientModal();
        setFormData({
          user_id: "",
          branch: 0,
          pregnancy_status: false,
          address: "",
          history_of_allergies: "",
          id_card: "",
        });
        toast.success("Client added successfully");
      },
      onError: (err) => {
        console.log(err);
      },
    });

  const { mutate: deleteClientMutation, isPending: deleteClientIsPending } =
    useMutation({
      mutationFn: async (clientId: number) => {
        if (!branch) return;
        console.log(clientId, branch?.hfId);
        const response = await deleteClient(branch?.hfId, clientId);
        return response;
      },
      onSuccess: async (data) => {
        await queryClient.invalidateQueries({
          queryKey: ["list-clients", branch?.hfId, page],
        });
      },
    });

  const { mutate: updateClientMutation, isPending: updateClientIsPending } =
    useMutation({
      mutationFn: async () => { },
      onSuccess: async (data) => {
        await queryClient.invalidateQueries({
          queryKey: ["list-clients", branch?.hfId, page],
        });
      },
    });

  const handleAddUser = () => {
    addClientMutation();
  };

  const handleDeleteUser = () => {
    if (!selectedUser) return;
    deleteClientMutation(selectedUser?.pk);
  };

  const handleEditUser = () => {
    updateClientMutation();
  };

  const isFormComplete = () => {
    return (
      formData.user_id &&
      formData.branch &&
      formData.address &&
      formData.history_of_allergies
    );
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleFieldChange = (
    field: keyof IAddUserForm,
    value: string | number | boolean
  ) => {
    setFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleFileUpload = (key: keyof IAddUserForm, file: File | null) => {
    setFormData((prevState) => ({
      ...prevState,
      [key]: file,
    }));
  };

  const handleTextFieldChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setFormData((prevState) => ({
      ...prevState,
      history_of_allergies: event.target.value,
    }));
  };

  const clientArray = allClients?.results
    .map((client) => ({
      label: client.fullname,
      value: client.pk,
      client,
    })) || [];

  const debounceSearch = useMemo(
    () =>
      debounce((search: string) => {
        setSearchTerm(search);
        setPage(1);
      }, 500, { leading: false, trailing: true }),
    []
  );

  useEffect(() => {
    return () => {
      debounceSearch.cancel();
    };
  }, [debounceSearch]);

  return {
    allClients,
    isLoading:
      allClientsLoading ||
      addClientIsPending ||
      deleteClientIsPending ||
      updateClientIsPending,
    error: allClientsError,
    branch,
    page,
    formData,
    isAddClientModal,
    isViewClientModal,
    allBranchesOptions,
    selectedUser,
    clientArray,
    setSelectedUser,
    openAddClientModal,
    openViewClientModal,
    closeAddClientModal,
    closeViewClientModal,
    setPage,
    handleAddUser,
    handleDeleteUser,
    handleEditUser,
    handleInputChange,
    handleFieldChange,
    handleTextFieldChange,
    handleFileUpload,
    isFormComplete,
    debounceSearch,
    handlePageChange,
    pagination,
    allClientsLoading
  };
}
